'use client';

import { memo, useEffect, type RefObject } from 'react';
import { motion, type MotionStyle, type Variants } from 'framer-motion';
import clsx from 'clsx';

const springConfig = {
  type: 'spring' as const,
  stiffness: 400,
  damping: 30,
  mass: 0.8,
};

export const tileContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.15,
    },
  },
};

export const tileEntryVariants = {
  hidden: {
    opacity: 0,
    scale: 0.7,
    y: 20,
    rotateX: -15,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    rotateX: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 350,
      damping: 25,
      mass: 0.8,
    },
  },
};

export const gameContentVariants = {
  hidden: {
    opacity: 0,
    x: 80,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      x: {
        type: 'spring' as const,
        stiffness: 350,
        damping: 30,
        mass: 0.7,
      },
      opacity: {
        duration: 0.25,
        ease: [0.0, 0.0, 0.2, 1] as [number, number, number, number],
      },
    },
  },
  exit: {
    opacity: 0,
    x: -80,
    transition: {
      x: {
        type: 'spring' as const,
        stiffness: 350,
        damping: 30,
        mass: 0.7,
      },
      opacity: {
        duration: 0.25,
        ease: [0.4, 0.0, 1, 1] as [number, number, number, number],
      },
    },
  },
};

export const celebrationContainerVariants = {
  idle: {},
  celebrate: {
    transition: {
      staggerChildren: 0.18,
      delayChildren: 0.08,
    },
  },
};

export const celebrationBounceVariants = {
  idle: {
    y: 0,
    scaleX: 1,
    scaleY: 1,
    opacity: 1,
  },
  celebrate: {
    y: [0, -32, -35, 0, -10, 0],
    scaleX: [1, 0.94, 0.96, 1.06, 0.98, 1],
    scaleY: [1, 1.08, 1.04, 0.92, 1.02, 1],
    opacity: [1, 1, 1, 1, 1, 1],
    transition: {
      duration: 1,
      ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
      times: [0, 0.25, 0.35, 0.6, 0.8, 1],
    },
  },
};

export const celebrationExplodeContainerVariants = {
  idle: {},
  celebrate: {
    transition: {
      staggerChildren: 0.18,
      delayChildren: 0.08,
    },
  },
};

const tileBaseStyles =
  'relative flex items-center justify-center rounded-3xl px-6 sm:px-8 py-3 border-b-10 transition-all duration-150';

interface TileProps {
  id: number;
  char: string;
  onClick: (id: number, char: string) => void;
  layoutId?: string;
  isDisabled?: boolean;
  sizeClassName?: string;
  lang?: string;
  variants?: Variants;
  motionStyle?: MotionStyle;
}

export const ActiveTile = ({
  id,
  char,
  onClick,
  layoutId,
  isDisabled,
  sizeClassName,
  lang,
  variants,
  motionStyle,
}: TileProps) => {
  return (
    <motion.button
      layoutId={layoutId ?? `${id}-${char}`}
      layout='position'
      type='button'
      onClick={() => onClick(id, char)}
      disabled={isDisabled}
      variants={variants}
      className={clsx(
        tileBaseStyles,
        'cursor-pointer transition-colors',
        'active:mb-[10px] active:translate-y-[10px] active:border-b-0',
        'border-(--secondary-color-accent) bg-(--secondary-color) text-(--background-color)',
        isDisabled && 'cursor-not-allowed opacity-50',
        sizeClassName,
      )}
      transition={springConfig}
      lang={lang}
      style={motionStyle}
    >
      {char}
    </motion.button>
  );
};

export const BlankTile = memo(
  ({ char, sizeClassName }: { char: string; sizeClassName?: string }) => {
    return (
      <div
        className={clsx(
          tileBaseStyles,
          'border-transparent bg-(--border-color)/30',
          'select-none',
          sizeClassName,
        )}
      >
        <span className='opacity-0'>{char}</span>
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.char === nextProps.char &&
    prevProps.sizeClassName === nextProps.sizeClassName,
);

BlankTile.displayName = 'BlankTile';

export type BottomBarState = 'check' | 'correct' | 'wrong';

const answerRowMinHeightClassNames = {
  // border-b-4 reduces the available content box by 2px versus the previous
  // border-b-2. The 5rem row needs those 2px back so the desktop kana tile
  // can enter without expanding the row and moving the underline.
  '5rem': 'min-h-[5.125rem]',
  '5.5rem': 'min-h-[5.5rem]',
} as const;

export const getAnswerRowClassName = (minHeight: '5rem' | '5.5rem' = '5rem') =>
  `flex ${answerRowMinHeightClassNames[minHeight]} w-full items-center border-b-3 border-(--border-color) px-2 pb-2 md:w-3/4 lg:w-2/3 xl:w-1/2`;

export const getGlassModeClassName = (
  baseClassName: string,
  isGlassMode: boolean,
) =>
  clsx(baseClassName, isGlassMode && 'rounded-xl bg-(--card-color) px-4 py-2');

export const useTilesModeActionKey = (
  buttonRef: RefObject<HTMLButtonElement | null>,
) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (
        event.key === 'Enter' ||
        event.code === 'Space' ||
        event.key === ' '
      ) {
        buttonRef.current?.click();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [buttonRef]);
};

// Temporary compatibility export while legacy imports are migrated.
export const useWordBuildingActionKey = useTilesModeActionKey;
